import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../../components/common/Header/Header";
import Modal from "../../../components/common/modal/modal";
import AboutYourHelper from "../../../components/newDomesticHelperPolicy/domesticHelperFlowSteps/aboutYourHelper/aboutYourHelper";
import ApplicationDetails from "../../../components/newDomesticHelperPolicy/domesticHelperFlowSteps/applicationDetails/applicationDetails";
import ContractDetails from "../../../components/newDomesticHelperPolicy/domesticHelperFlowSteps/contractDetails/contractDetails";
import CoverageCustomization from "../../../components/newDomesticHelperPolicy/domesticHelperFlowSteps/coverageCustomization/coverageCustomization";
import Documents from "../../../components/newDomesticHelperPolicy/domesticHelperFlowSteps/documents/documents";
import HelperAdditionalInfo from "../../../components/newDomesticHelperPolicy/domesticHelperFlowSteps/helperAdditionalInfo/helperAdditionalInfo";
import HelperBasicDetails from "../../../components/newDomesticHelperPolicy/domesticHelperFlowSteps/helperBasicDetails/helperBasicDetails";
import HelperEmploymentDetails from "../../../components/newDomesticHelperPolicy/domesticHelperFlowSteps/helperEmploymentDetails/helperEmploymentDetails";
import PlanSelection from "../../../components/newDomesticHelperPolicy/domesticHelperFlowSteps/planSelection/planSelection";
import DomesticHelperStartPage from "../../../components/newDomesticHelperPolicy/domesticHelperFlowSteps/startPage/startPage";
import Summary from "../../../components/newDomesticHelperPolicy/domesticHelperFlowSteps/summary/summary";
import useDomesticHelperFlow from "../../../hooks/useDomesticHelperFlowSimple";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import { resetDomesticHelperFlow } from "../../../redux/features/domesticHelperFlowSlice";
import { clearDomesticHelperFormData } from "../../../redux/features/domesticHelperFormSlice";
import { clearDomesticHelperFiles } from "../../../utils/domesticHelperFilesStorage";
import {
  headerStepperData,
  STEP_NAME_ABOUT_YOUR_HELPER,
  STEP_NAME_APPLICATION_DETAILS,
  STEP_NAME_CONTRACT_DETAILS,
  STEP_NAME_COVERAGE_OPTIONS,
  STEP_NAME_DOCUMENTS,
  STEP_NAME_HELPER_ADDITIONAL,
  STEP_NAME_HELPER_BASIC,
  STEP_NAME_HELPER_EMPLOYMENT,
  STEP_NAME_PLAN_SELECTION,
  STEP_NAME_START,
  STEP_NAME_SUMMARY,
} from "./data";

const NewDomesticHelperPolicy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();
  const [showModal, setShowModal] = useState(true);
  const { currentStep, currentSubstep } = useDomesticHelperFlow();

  // Clear payment redirect flag on component mount (user returned from payment or started fresh)
  useEffect(() => {
    const wasRedirectingToPayment = sessionStorage.getItem("isRedirectingToPayment");
    if (wasRedirectingToPayment === "true") {
      console.log("🔓 Clearing payment redirect flag - user returned or component mounted");
      sessionStorage.removeItem("isRedirectingToPayment");
    }
  }, []);

  // Clear form data when component unmounts or when user navigates away
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset - redirecting to payment");
        return;
      }
      dispatch(resetDomesticHelperFlow());
      dispatch(clearDomesticHelperFormData({}));
      clearDomesticHelperFiles();
    };

    const handlePopState = () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset - redirecting to payment");
        return;
      }
      dispatch(resetDomesticHelperFlow());
      dispatch(clearDomesticHelperFormData({}));
      clearDomesticHelperFiles();
    };

    // Add event listeners for browser navigation
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    // Cleanup function - runs when component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);

      // CRITICAL: Don't clear flow state if we're redirecting to payment
      // This prevents the brief flash of the start screen before redirect
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset on unmount - redirecting to payment");
        return;
      }

      // Clear form data and stored files when component unmounts
      dispatch(resetDomesticHelperFlow());
      dispatch(clearDomesticHelperFormData({}));
      clearDomesticHelperFiles();
    };
  }, [dispatch]);

  // Clear form data when user navigates away from the domestic helper policy route
  useEffect(() => {
    const currentPath = location.pathname;

    return () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset on route change - redirecting to payment");
        return;
      }

      // Only clear if we're leaving the domestic helper policy routes
      if (!currentPath.includes("/domestic-helper-policy")) {
        dispatch(resetDomesticHelperFlow());
        dispatch(clearDomesticHelperFormData({}));
      }
    };
  }, [location.pathname, dispatch]);

  const handleCloseModal = () => {
    // Reset domestic helper flow and form data when modal is closed
    dispatch(resetDomesticHelperFlow());
    dispatch(clearDomesticHelperFormData({}));
    // Clear temporarily stored files (Passport, Work Permit)
    clearDomesticHelperFiles();
    setShowModal(false);
    navigate("/");
  };

  // Don't hide the header for any step
  const shouldHideHeader = false;

  const PageRenderer = () => {
    switch (currentStep?.name) {
      case STEP_NAME_START:
        return <DomesticHelperStartPage onClose={handleCloseModal} />;
      case STEP_NAME_APPLICATION_DETAILS:
        return <ApplicationDetails />;
      case STEP_NAME_ABOUT_YOUR_HELPER:
        return <AboutYourHelper />;
      case STEP_NAME_HELPER_BASIC:
        return <HelperBasicDetails />;
      case STEP_NAME_HELPER_EMPLOYMENT:
        return <HelperEmploymentDetails />;
      case STEP_NAME_HELPER_ADDITIONAL:
        return <HelperAdditionalInfo />;
      case STEP_NAME_CONTRACT_DETAILS:
        return <ContractDetails />;
      case STEP_NAME_PLAN_SELECTION:
        return <PlanSelection />;
      case STEP_NAME_COVERAGE_OPTIONS:
        return <CoverageCustomization />;
      case STEP_NAME_DOCUMENTS:
        return <Documents />;
      case STEP_NAME_SUMMARY:
        // Handle substeps for Summary step (review_summary, payment_methods)
        if (currentSubstep === "review_summary") {
          return <Summary />;
        }
        // Default to review summary
        return <Summary />;
      default:
        return <DomesticHelperStartPage onClose={handleCloseModal} />;
    }
  };

  const activeStepData = useMemo(() => {
    // Map detailed flow steps to stepper display steps
    let stepperKey = currentStep?.name;

    // Map actual flow steps to stepper display steps
    // Stepper: Start → Details → Helper → Documents → Summary

    // APPLICATION_DETAILS maps to "Details"
    if (currentStep?.name === STEP_NAME_APPLICATION_DETAILS) {
      stepperKey = STEP_NAME_APPLICATION_DETAILS;
    }

    // ABOUT_YOUR_HELPER maps to "Helper"
    if (currentStep?.name === STEP_NAME_ABOUT_YOUR_HELPER) {
      stepperKey = STEP_NAME_ABOUT_YOUR_HELPER;
    }

    // DOCUMENTS maps to itself
    if (currentStep?.name === STEP_NAME_DOCUMENTS) {
      stepperKey = STEP_NAME_DOCUMENTS;
    }

    // STEP_NAME_SUMMARY maps to itself in the stepper
    if (currentStep?.name === STEP_NAME_SUMMARY) {
      stepperKey = STEP_NAME_SUMMARY;
    }

    let activeStep = headerStepperData.find((each) => each.key === stepperKey);
    return activeStep;
  }, [currentStep]);

  return (
    <Modal show={showModal}>
      {!shouldHideHeader && (
        <div className="relative">
          <Header
            isPopup
            hideMobileClose
            onCloseModal={handleCloseModal}
            showStepper={true}
            steps={headerStepperData}
            activeStep={activeStepData}
          />
        </div>
      )}
      <div
        className={`mainDomesticHelperModal relative ${
          shouldHideHeader ? "pt-0" : "pt-5"
        } pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-[30%] max767:h-screen ipad:pt-[10%] ipad:h-[93vh] ipad:overflow-y-scroll max767:pt-0`}
      >
        {/* "wait", not "popLayout" — the same change Medical and Ta'asheera
            needed. popLayout keeps the outgoing step mounted while the incoming
            one renders, and both portal a sticky footer into #dh-footer-portal
            and a mobile back button into #dh-back-portal, so for the length of
            the exit two of each are stacked on the same pixels — one showing
            the previous step's button label, price and add-ons. popLayout also
            takes the exiting step out of layout flow (position: absolute), so
            the container height snaps to the incoming step's in the same frame.
            Waiting for the exit to finish means only one step is ever mounted.
            A tap landing in that overlap could also reach the outgoing step's
            handler and advance the flow twice, skipping a page. */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`${currentStep?.name || "start"}-${currentSubstep || ""}`}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            {PageRenderer()}
          </motion.div>
        </AnimatePresence>
        {/* Stable mount point for the DH bottom sticky footer button.
            Kept outside AnimatePresence so the button never sits inside the
            transformed (sliding) wrapper, which would cause it to flicker. */}
        <div id="dh-footer-portal" />
        {/* Stable mount point for the DH JourneyLayout mobile fixed back/X button. */}
        <div id="dh-back-portal" />
      </div>
    </Modal>
  );
};

export default NewDomesticHelperPolicy;
